import PaginatedList from '@theme/paginated-list';

const EXCERPT_SELECTOR = '.blog-post-card__excerpt';

/**
 * A custom element that renders a paginated blog posts list.
 *
 * Frelan addition: the card excerpt is the flexible part of the card — it
 * fills whatever height the grid row leaves after the title. That height is
 * rarely a whole number of lines, so each excerpt is clamped to the number of
 * full lines that fit, measured live. Cards added by the infinite scroll are
 * picked up through a MutationObserver.
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {ResizeObserver | null} */
  #excerptObserver = null;
  /** @type {MutationObserver | null} */
  #cardObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#excerptObserver = new ResizeObserver((entries) => {
      for (const entry of entries) this.#clamp(/** @type {HTMLElement} */ (entry.target));
    });

    this.#cardObserver = new MutationObserver(() => this.#observeExcerpts());
    this.#cardObserver.observe(this, { childList: true, subtree: true });

    this.#observeExcerpts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#excerptObserver?.disconnect();
    this.#cardObserver?.disconnect();
    this.#excerptObserver = this.#cardObserver = null;
  }

  #observeExcerpts() {
    if (!this.#excerptObserver) return;
    this.querySelectorAll(`${EXCERPT_SELECTOR}:not([data-clamped])`).forEach((el) => {
      el.setAttribute('data-clamped', '');
      this.#excerptObserver?.observe(el);
    });
  }

  /**
   * Clamp an excerpt to the whole lines that fit its flex-assigned height.
   * @param {HTMLElement} el
   */
  #clamp(el) {
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    if (!lineHeight || !el.clientHeight) return;
    const lines = Math.max(1, Math.floor((el.clientHeight + 1) / lineHeight));
    el.style.setProperty('--excerpt-lines', String(lines));
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
